<template>
	<section class="income-through-dividends bg-color-content-primary marketing-section text-color-white">
		<div class="container">
			<div class="col-12 col-10-xl mx-auto">
				<header class="col-12 col-6-lg mb-200-md mb-200">
					<h2 class="heading-1 text-color-white"> Income through dividends </h2>

					<p class="body-md mt-100">
						Real estate allows investors to accrue a unique mix of both long-term appreciation and income
						potential. The figures here represent the income portion of advisory client returns through
						Fundrise and do not include appreciation over time.
					</p>
				</header>
				<div
					class="dividends-section display-flex align-items-center flex-wrap-wrap gutters-300-md position-relative flex-direction-column-reverse-smo"
				>
					<div class="col-12 col-6-md position-relative text-color-white">
						<table class="distributions-table table body-sm text-align-right">
							<thead>
								<tr class="text-color-white font-weight-bold">
									<th>Investment objective</th>
									<th class="text-color-white text-align-right width-160px">
										Currently declared annualized yield
									</th>
									<th class="text-align-right width-160px">
										12 months ending <br />
										<BasePlaceHolderInline :placehold="!dividendsDistributionsLoaded">
											{{ trailingTwelveMonthEndDate }}</BasePlaceHolderInline
										>
									</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="(obj, o) in objectiveYields" :key="o">
									<td class="text-align-left pl-25">{{ obj.label }}</td>
									<td> {{ obj.currentYield }}% </td>
									<td> {{ obj.trailingAnnualYield }}% </td>
								</tr>
							</tbody>
						</table>
					</div>
					<div class="cumulative-dividends col-12 col-6-md text-align-center order--1-smo mt-300-smo">
						<div class="bg-img-dotted-muted p-200-md p-150-smo ml-300-lg position-relative">
							<BaseSvgIcon name="hd-coin" width="53" height="53" :fill="false" class="coin-wrapper" />
							<div class="bg-color-content-primary px-100 pt-250 pb-200">
								<p>
									<BasePlaceHolderInline
										:placehold="!revealCumulativeDividends"
										:theme="'dark'"
										:class="[
											'heading-xl  justify-content-center',
											revealCumulativeDividends ? 'display-flex' : 'display-block'
										]"
									>
										<span class="heading-2 text-color-white pr-25 pt-25">$</span>
										{{ currency(cumulativeNetDividends, 0, '') }}
										<span
											v-if="cumulativeNetDividendsDecimal"
											class="heading-2 text-color-white pl-25"
											>.{{ cumulativeNetDividendsDecimal }}</span
										>
									</BasePlaceHolderInline>
									<span class="body-sm mt-50"
										>Cumulative net distributions earned by advisory clients</span
									>
								</p>
							</div>
						</div>
					</div>
				</div>

				<p class="mt-300 text-align-center text-color-gray-40 body-xs">
					<BaseButton
						classes="text-link--dark-mode text-color-gray-40"
						data-test="dividends-section-disclosure-prompt"
						@click="openModal(dividendsDisclosureModalProps)"
					>
						Learn more</BaseButton
					>
					about the assumptions in this section. The foregoing performance information is presented solely
					with regard to the advisory client performance of the clients of Fundrise Advisors, LLC pursuant to
					the Investment Advisers Act of 1940, and does not represent the performance of any individual
					investor or any individual or aggregate performance of any funds with offerings qualified pursuant
					Regulation A of the Securities Act (the "Regulation A Funds"). For more information about the
					Regulation A Funds, and their corresponding Forms 1-A, including their individual performance
					information, please click
					<BaseButton
						classes="text-link--dark-mode text-color-gray-40"
						data-test="dividends-section-disclosure-prompt"
						@click="openModal(regAFundListing)"
					>
						here</BaseButton
					>.
				</p>
			</div>
		</div>
	</section>
</template>
<script setup lang="ts">
import { computed, ref, Ref } from 'vue';
import { currency, number } from '@filters/shared-filters';
import { DividendDistributions, ObjectiveYield } from 'types/historical-performance';
import { getCumulativeDividends, getDividendsDistributions } from '@api/historical-performance';
import { accountFundObjective } from '@constants/account-info';
import { CustomSpacetimeFormat } from 'types/spacetime-formats';
import { regAFundListing } from '@constants/marketing-site';
import spacetime from 'spacetime';
import { useIdleCallback } from '@utils/composables/use-idle-callback';

interface Emits {
	(e: 'open-modal', value: object): void;
}

interface FormattedObjectiveYield {
	label: string;
	currentYield: string;
	trailingAnnualYield: string;
}

const emit = defineEmits<Emits>();

const dividendsDisclosureModalProps = {
	content: [
		'The “currently declared annualized yield” is the weighted average current annualized yield of all clients’ portfolio allocations having the indicated objective as of the present day',
		'The “past 12 months yield” shows the weighted average yield of all clients’ portfolio allocations having the indicated objective for the 12 months ending on the indicated date.',
		'“Cumulative net dividends earned” is the total value of dividends earned by clients of Fundrise advisors, net of fees. Note that, as dividends are typically distributed following the end of each quarter, a portion of the dividends shown at any time may have been earned but not yet distributed.'
	],
	centeredContent: false
};

const cumulativeNetDividends: Ref<number | null> = ref(null);

const cumulativeNetDividendsDecimal = ref('');

const dividendsDistributionsLoaded = ref(false);

const dividendsDistributions: Ref<DividendDistributions | null> = ref(null);

const revealCumulativeDividends = computed((): boolean => cumulativeNetDividends.value !== null);

const trailingTwelveMonthEndDate = computed((): string => {
	return (
		spacetime(dividendsDistributions?.value?.trailingTwelveMonthEndDate).format(
			CustomSpacetimeFormat.MONTH_DATE_YEAR
		) ?? ''
	);
});

const objectiveYields = computed((): Array<FormattedObjectiveYield> => {
	const objectDistributions: Array<FormattedObjectiveYield> = [];

	const distributions = dividendsDistributions?.value;

	const sortedDistributions =
		distributions?.yieldsByFundObjective.sort((a, b) => {
			return b.currentlyDeclaredYield.localeCompare(a.currentlyDeclaredYield);
		}) ?? [];

	sortedDistributions.forEach((obj: ObjectiveYield) => {
		objectDistributions.push({
			label: accountFundObjective[obj.fundObjective],
			currentYield: number(obj.currentlyDeclaredYield, 2),
			trailingAnnualYield: number(obj.trailingTwelveMonthYield, 2)
		});
	});
	return objectDistributions;
});

function openModal(modalProps: object): void {
	emit('open-modal', modalProps);
}

useIdleCallback(async () => {
	try {
		let netDividends;

		[netDividends, dividendsDistributions.value] = await Promise.all([
			getCumulativeDividends(),
			getDividendsDistributions()
		]);

		cumulativeNetDividends.value = netDividends.cumulativeDividendsThroughToday;
		cumulativeNetDividendsDecimal.value = cumulativeNetDividends.value.toString().split('.')[1];

		dividendsDistributionsLoaded.value = true;
	} catch {}
});
</script>
<style lang="scss" scoped>
@use '../../../styles/utilities/respond-to.scss' as *;
@use '../../../styles/constants/colors' as *;

.income-through-dividends {
	.width-160px {
		width: 160px;
	}

	.dividends-section {
		position: relative;

		.cumulative-dividends {
			@include respond-to(lg) {
				transform: translateY(-35%);
			}

			.coin-wrapper {
				position: absolute;
				margin: 0 auto;
				left: 0;
				right: 0;
				top: -10px;
			}
		}

		.distributions-table {
			@include respond-to(lg) {
				width: auto;
			}

			thead {
				vertical-align: bottom;

				tr {
					border-bottom: 1px solid;
					border-top-color: token('content-tertiary');
				}
			}

			tbody {
				tr {
					&:not(:first-child) {
						td {
							border-top: none;
							border-top-color: token('content-tertiary');
							background-image: repeating-linear-gradient(
									to right,
									token('content-tertiary') 0%,
									token('content-tertiary') 50%,
									transparent 50%,
									transparent 100%
								),
								repeating-linear-gradient(
									to right,
									token('content-tertiary') 0%,
									token('content-tertiary') 50%,
									transparent 50%,
									transparent 100%
								);
							background-position: top;
							background-repeat: repeat-x;
							background-size:
								0,
								5px 1px;
						}
					}
				}
			}
		}
	}
}
</style>
